import { useCallback, useState } from "react";
import { message } from "antd";
import {
  BRIEF_CONFIDENCE_FLOOR,
  briefLockReason,
  isBriefLocked,
} from "./confidenceGating";

/**
 * Shared action handlers + transient UI state for a creative node.
 * Used by AdDetailModal (full popup) and TheWebPage's inline side panel.
 *
 * Two live actions, both run inline (nested modals) so they don't depend on
 * any other page existing:
 *   • Generate variation brief — analyzes the video + drop-off and rewrites
 *     the script (see BriefDraftModal).
 *   • Send to Brain — extracts the script + key frames and runs the Tribe v2
 *     neural scan, then banks the learnings in the KB (see BrainScanModal).
 *
 * The unexplored branch keeps its lightweight placeholder actions.
 */
export const useCreativeActions = (node) => {
  const [briefOpen, setBriefOpen] = useState(false);
  const [brainOpen, setBrainOpen] = useState(false);

  const briefLocked = isBriefLocked(node);

  const handleBrief = useCallback(() => {
    if (briefLocked) {
      message.warning({ content: briefLockReason(node), duration: 5 });
      return;
    }
    setBriefOpen(true);
  }, [briefLocked, node]);

  const handleSendToBrain = useCallback(() => {
    if (!node) return;
    setBrainOpen(true);
  }, [node]);

  const handleReferenceWinners = useCallback(() => {
    message.info({
      content: "Reference-winner lookup ships in Phase 4 (similarity search).",
      duration: 3,
    });
  }, []);

  const handleAddToTestQueue = useCallback(() => {
    message.success({
      content: "Added to test queue. Briefs page will show this when it lands.",
      duration: 3,
    });
  }, []);

  const closeBrief = useCallback(() => setBriefOpen(false), []);
  const closeBrain = useCallback(() => setBrainOpen(false), []);

  return {
    briefOpen,
    closeBrief,
    brainOpen,
    closeBrain,
    briefLocked,
    handleBrief,
    handleSendToBrain,
    handleReferenceWinners,
    handleAddToTestQueue,
  };
};

export { BRIEF_CONFIDENCE_FLOOR };
